import React from 'react';

const PrivacyPolicy = () => {
  const lastUpdatedDate = "May 29, 2025"; // REPLACE WITH ACTUAL LAST UPDATED DATE
  const effectiveDate = "May 29, 2025"; // REPLACE WITH ACTUAL EFFECTIVE DATE

  // Helper for consistent section styling
  const Section = ({ title, children }) => (
    <section className="mb-8">
      <h2 className="text-2xl sm:text-3xl font-semibold text-gray-800 dark:text-gray-100 mb-4 border-b border-gray-300 dark:border-gray-700 pb-2">
        {title}
      </h2>
      <div className="space-y-4 text-gray-700 dark:text-gray-300 leading-relaxed">
        {children}
      </div>
    </section>
  );

  return (
    <div className="bg-gray-50 dark:bg-gray-900 min-h-screen">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-20">
        <header className="mb-10 text-center">
          <h1 className="text-4xl sm:text-5xl font-extrabold text-gray-900 dark:text-white">
            Privacy Policy for <span className="text-blue-600 dark:text-blue-400">TabDivider</span>
          </h1>
          <p className="mt-4 text-md sm:text-lg text-gray-600 dark:text-gray-400">
            Last Updated: {lastUpdatedDate}
          </p>
          <p className="mt-1 text-md sm:text-lg text-gray-600 dark:text-gray-400">
            Effective Date: {effectiveDate}
          </p>
        </header>

        <div className="bg-white dark:bg-gray-800 shadow-xl rounded-lg p-6 sm:p-8 lg:p-10">
          <Section title="1. Introduction">
            <p>
              Welcome to TabDivider ("we," "us," "our"). We are committed to protecting your personal information and your right to privacy. This Privacy Policy governs your use of our website, <a href="https://tabdivider.com" className="text-blue-600 dark:text-blue-400 hover:underline">tabdivider.com</a> (the "Site"), and our bill splitting and shared expense management services (collectively, the "Services").
            </p>
            <p>
              This policy explains how we collect, use, disclose, and safeguard your information when you use our Services. Please read this privacy policy carefully. If you do not agree with the terms of this privacy policy, please do not access the site or use our Services.
            </p>
            <p className="font-semibold mt-2">
              By using TabDivider, you signify your acceptance of this Privacy Policy.
            </p>
          </Section>

          <Section title="2. Information We Collect">
            <p>
              We may collect information about you in a variety of ways. The information we may collect via the Services depends on the content and materials you use, and includes:
            </p>
            <h3 className="text-xl font-semibold text-gray-700 dark:text-gray-200 mt-4 mb-2">A. Personal Data</h3>
            <p>
              Personally identifiable information, such as your name, email address, and other details you may provide when you register with our Services (e.g., through Supabase authentication), participate in bill splitting activities, or otherwise interact with us. You are under no obligation to provide us with personal information of any kind; however, your refusal to do so may prevent you from using certain features of the Services.
            </p>
            <h3 className="text-xl font-semibold text-gray-700 dark:text-gray-200 mt-4 mb-2">B. Bill and Expense Data</h3>
            <p>
              Information related to the bills you manage through TabDivider, including:
            </p>
            <ul className="list-disc list-inside ml-4 space-y-1">
              <li>Receipt details (items, prices, vendor names) you manually enter or upload.</li>
              <li>Information extracted via Optical Character Recognition (OCR) from uploaded receipts. This data is processed to populate bill items and is associated with your bill. We strive to minimize the collection and storage of sensitive information from receipts.</li>
              <li>Details of how bills are split, including assignments to individuals and payment statuses.</li>
              <li>Currency and region preferences for bill calculations.</li>
            </ul>
            <h3 className="text-xl font-semibold text-gray-700 dark:text-gray-200 mt-4 mb-2">C. Derivative Data (Usage Data)</h3>
            <p>
              Information our servers automatically collect when you access the Services, such as your IP address, browser type, operating system, access times, and the pages you have viewed directly before and after accessing the Site. If you are using our mobile application, this information may also include your device name and type, operating system, phone number, country, likes and replies to a post, and other interactions with the application and other users via server log files, as well as any other information you choose to provide.
            </p>
             <h3 className="text-xl font-semibold text-gray-700 dark:text-gray-200 mt-4 mb-2">D. Data from Third-Party Services (e.g., Supabase)</h3>
            <p>
              We may use third-party services like Supabase for authentication and database management. When you create an account or log in using such services, we may receive information from them, such as your email address or user ID, as permitted by their privacy policies and your privacy settings with those services.
            </p>
            <h3 className="text-xl font-semibold text-gray-700 dark:text-gray-200 mt-4 mb-2">E. Cookies and Tracking Technologies</h3>
            <p>
              We may use cookies, web beacons, tracking pixels, and other tracking technologies on the Site and our mobile application to help customize the Services and improve your experience. For more information on how we use cookies, please refer to our Cookie Policy [Link to Cookie Policy if you have one, otherwise detail here].
            </p>
          </Section>

          <Section title="3. How We Use Your Information">
            <p>
              Having accurate information about you permits us to provide you with a smooth, efficient, and customized experience. Specifically, we may use information collected about you via the Services to:
            </p>
            <ul className="list-disc list-inside ml-4 space-y-1">
              <li>Create and manage your account.</li>
              <li>Provide, operate, and maintain our Services, including bill splitting and expense tracking functionalities.</li>
              <li>Process your transactions and manage your shared bills.</li>
              <li>Improve, personalize, and expand our Services.</li>
              <li>Understand and analyze how you use our Services for development and optimization.</li>
              <li>Develop new products, services, features, and functionality.</li>
              <li>Communicate with you, either directly or through one of our partners, including for customer service, to provide you with updates and other information relating to the Services, and for marketing and promotional purposes (with your consent where required).</li>
              <li>Send you emails and push notifications (if applicable).</li>
              <li>Find and prevent fraud and ensure the security of our Services.</li>
              <li>Comply with legal obligations and enforce our terms and conditions.</li>
            </ul>
          </Section>

          <Section title="4. Disclosure of Your Information">
            <p>
              We may share information we have collected about you in certain situations. Your information may be disclosed as follows:
            </p>
            <h3 className="text-xl font-semibold text-gray-700 dark:text-gray-200 mt-4 mb-2">A. By Law or to Protect Rights</h3>
            <p>
              If we believe the release of information about you is necessary to respond to legal process, to investigate or remedy potential violations of our policies, or to protect the rights, property, and safety of others, we may share your information as permitted or required by any applicable law, rule, or regulation.
            </p>
            <h3 className="text-xl font-semibold text-gray-700 dark:text-gray-200 mt-4 mb-2">B. Third-Party Service Providers</h3>
            <p>
              We may share your information with third parties that perform services for us or on our behalf, including data storage (e.g., Supabase), OCR processing, data analysis, email delivery, hosting services, customer service, and marketing assistance. These service providers are authorized to use your personal information only as necessary to provide these services to us.
            </p>
            <h3 className="text-xl font-semibold text-gray-700 dark:text-gray-200 mt-4 mb-2">C. With Other Users in Your Group</h3>
            <p>
              When you share bills or participate in group expenses, information related to those bills (such as item details, amounts, and who is assigned to what) will be visible to other members of that specific bill or group. Your name or identifier associated with your account will also be visible.
            </p>
            <h3 className="text-xl font-semibold text-gray-700 dark:text-gray-200 mt-4 mb-2">D. Business Transfers</h3>
            <p>
              We may share or transfer your information in connection with, or during negotiations of, any merger, sale of company assets, financing, or acquisition of all or a portion of our business to another company.
            </p>
             <h3 className="text-xl font-semibold text-gray-700 dark:text-gray-200 mt-4 mb-2">E. With Your Consent</h3>
            <p>
              We may disclose your personal information for any other purpose with your consent.
            </p>
          </Section>

          <Section title="5. Data Security">
            <p>
              We use administrative, technical, and physical security measures to help protect your personal information. While we have taken reasonable steps to secure the personal information you provide to us, please be aware that despite our efforts, no security measures are perfect or impenetrable, and no method of data transmission can be guaranteed against any interception or other type of misuse. Any information disclosed online is vulnerable to interception and misuse by unauthorized parties. Therefore, we cannot guarantee complete security if you provide personal information.
            </p>
          </Section>

          <Section title="6. Data Retention">
            <p>
              We will retain your personal information only for as long as is necessary for the purposes set out in this Privacy Policy. We will retain and use your information to the extent necessary to comply with our legal obligations (for example, if we are required to retain your data to comply with applicable laws), resolve disputes, and enforce our legal agreements and policies. Bill data that you create may be retained as long as your account is active or as needed to provide you Services. You can delete your account and associated data as described in "Your Data Protection Rights".
            </p>
          </Section>


          <Section title="7. Your Data Protection Rights">
            <p>
              Depending on your location, you may have the following rights regarding your personal information:
            </p>
            <ul className="list-disc list-inside ml-4 space-y-1">
              <li>The right to access – You have the right to request copies of your personal data.</li>
              <li>The right to rectification – You have the right to request that we correct any information you believe is inaccurate or complete information you believe is incomplete.</li>
              <li>The right to erasure – You have the right to request that we erase your personal data, under certain conditions.</li>
              <li>The right to restrict processing – You have the right to request that we restrict the processing of your personal data, under certain conditions.</li>
              <li>The right to object to processing – You have the right to object to our processing of your personal data, under certain conditions.</li>
              <li>The right to data portability – You have the right to request that we transfer the data that we have collected to another organization, or directly to you, under certain conditions.</li>
            </ul>
            <p className="mt-2">
              If you make a request, we have one month to respond to you. If you would like to exercise any ofthese rights, please contact us at our contact information below.
            </p>
             <p className="mt-2">
              You also have the right to delete your account and associated data through the account settings in our Service, if available, or by contacting us.
            </p>
          </Section>

          <Section title="8. Policy for Children">
            <p>
              We do not knowingly solicit information from or market to children under the age of 13 (or other age as required by local law). If you become aware of any data we have collected from children under the relevant age, please contact us using the contact information provided below.
            </p>
          </Section>

          <Section title="9. Changes to This Privacy Policy">
            <p>
              We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date at the top of this Privacy Policy. You are advised to review this Privacy Policy periodically for any changes. Changes to this Privacy Policy are effective when they are posted on this page.
            </p>
            <p>
              Your continued use of the Services after the posting of changes constitutes your acceptance of such changes.
            </p>
          </Section>

          <Section title="10. Contact Us">
            <p>
              If you have questions or comments about this Privacy Policy, or if you wish to exercise your data protection rights, please contact us at:
            </p>
            <p className="mt-2">
              TabDivider<br />
              Email: <a href="mailto:privacy@tabdivider.com" className="text-blue-600 dark:text-blue-400 hover:underline">privacy@tabdivider.com</a><br />
              {/* Add physical address if applicable/required */}
              {/* [Your Company Address Line 1] */}
              {/* [Your Company Address Line 2] */}
              {/* [City, State, Zip Code] */}
            </p>
          </Section>

          <div className="mt-12 pt-6 border-t border-gray-200 dark:border-gray-700 text-center text-sm text-gray-500 dark:text-gray-400">
            <p>
              This Privacy Policy is a foundational document for trust between TabDivider and its users. We encourage you to read it carefully.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;